import React, { useImperativeHandle, useRef, useState } from 'react';
import intl from 'react-intl-universal';
import { Button } from 'antd';
import CustomDrawer, { IdrawerRef } from '@/components/CustomDrawer';
import EditCellTable, { IcustomColumnType, IshouldEditCellProps } from '@/components/EditCellTable';
import { fetchUpdateRefoundConsume } from '@/api/transaction';
import { fetchRefundCrossNightList } from '@/api/bill/refund';
import { DATE_TIME_FORMAT } from '@/config';
import { parseFen2Currency } from '@/utils/number';
import { getTimeByUtcOffset } from '@/utils/intl';
import getRandomId from '@/utils/uuid';
import RefundDetailDrawer from '../RefundDetailDrawer';

const AcrossNightRefundDrawer = React.forwardRef((props, ref) => {
  const [dataSource, setDataSource] = useState([]);
  const [loading, setLoading] = useState(false);

  const drawerRef = useRef<IdrawerRef>();
  const refundRef = useRef<any>();

  const initList = async (resvNo) => {
    try {
      setLoading(true);
      const res = await fetchRefundCrossNightList({ resvNo });
      if (res.code === 0) {
        setDataSource(res.data || []);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const handleOpenDrawer = ({ resvNo }) => {
    drawerRef.current.openDrawer();
    initList(resvNo);
  };

  const handleHourSaveChange = async (values, index) => {
    const { refundOrderNo, remark } = values[index] || {};
    if (refundOrderNo) {
      const res = await fetchUpdateRefoundConsume({ no: refundOrderNo, remark });
      if (res.code === 0) {
        setDataSource([...values]);
      }
    }
  };

  useImperativeHandle(
    ref,
    () => ({
      ...drawerRef.current,
      openDrawer: handleOpenDrawer,
    }),
    [],
  );

  const columns: Array<IcustomColumnType<any>> = [
    { title: intl.get('reports_cross_refund_col_tit8'), dataIndex: 'refundOrderNo' },
    {
      title: intl.get('order_frontDesk_label_bill_enter_account_type'),
      dataIndex: 'orderType',
    },
    { title: intl.get('order_frontDesk_label_subjectName'), dataIndex: 'subjectName' },
    { title: intl.get('order_frontDesk_label_paymentType'), dataIndex: 'paymentTypeName' },
    { title: intl.get('order_frontDesk_label_payDetail'), dataIndex: 'payDetail' },
    {
      title: intl.get('order_action_pay'),
      dataIndex: 'payAmount',
      render: (val) => parseFen2Currency(-val) || '-',
    },
    {
      title: intl.get('order_time'),
      dataIndex: 'refundTime',
      render: (val) => (val ? getTimeByUtcOffset(val, DATE_TIME_FORMAT) : '-'),
    },
    { title: intl.get('order_frontDesk_createBy'), dataIndex: 'operator' },
    {
      title: intl.get('order_frontDesk_label_bill_remark'),
      dataIndex: 'remark',
      shouldEditCell: (): IshouldEditCellProps => {
        return {
          editable: true,
          cellEditMode: 'popConfirm',
        };
      },
    },
    {
      title: intl.get('order_action'),
      render: (_, record) => (
        <Button
          type="link"
          onClick={() =>
            refundRef.current.openDrawer({
              ...record,
              no: record.refundOrderNo,
            })
          }
        >
          {intl.get('bill_cross_refound_detail')}
        </Button>
      ),
      align: 'right',
      fixed: 'right',
    },
  ];

  return (
    <>
      <CustomDrawer
        width="70%"
        title={intl.get('bill_relevancy_across_night_refund_order')}
        ref={drawerRef}
        footer={
          <>
            <Button type="ghost" onClick={() => drawerRef.current.closeDrawer()}>
              {intl.get('order_action_close')}
            </Button>
          </>
        }
      >
        <RefundDetailDrawer ref={refundRef} />

        <div className="space-between" style={{ margin: '0 0 16px' }}>
          <div className="groupLabel">{intl.get('bill_relevancy_across_night_refund_order')}</div>
        </div>
        <EditCellTable
          rowKey={() => getRandomId()}
          columns={columns}
          dataSource={dataSource}
          loading={loading}
          onSaveCellChange={handleHourSaveChange}
          pagination={false}
          scroll={{ x: 'max-content' }}
        />
      </CustomDrawer>
    </>
  );
});

export default AcrossNightRefundDrawer;
